import React, { useEffect } from 'react';
import CopyCard from './CopyCard';

export default function OpenView({ closesAt, now }) {
  useEffect(() => {
    // Inject the supporting script for the embedded chatbot
    const scriptId = 'chatbot-snippet-bundle';
    
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.id = scriptId;
      script.src = 'https://emb-chat.noform.ai/snippet-bundle.js';
      document.body.appendChild(script);
    }

    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const daysRemaining = Math.ceil((closesAt - now) / (1000 * 60 * 60 * 24));
  const isEndingSoon = daysRemaining < 7;

  const sampleQuestions = [
    "My best performer just missed a third deadline. What do I say on Monday?",
    "How do I tell someone their review rating is lower than last year?",
    "My manager keeps adding to my plate. How do I say no without sounding difficult?",
    "I inherited a team that preferred my predecessor. Where do I start?"
  ];

  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <header className="w-full max-w-4xl mx-auto px-6 py-8 flex justify-between items-center border-b border-hairline font-display">
        <a href="https://www.keyfutureskills.com" className="flex items-center">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/27e565ca62f331bb3d3ca67ccd492f06cd2fa92619ed72ab1d77f8d98c6e5b4cc6657f2d778fcddfd847ef506ea1c4d1/images/aW1hZ2UvcG5n/cb031a7724b5418947317fb28beca1c5.png" 
            alt="KeyFutureSkills Logo" 
            className="h-10 w-auto object-contain"
          />
        </a>
        <a 
          href="https://member.keyfutureskills.com" 
          className="text-sm font-medium hover:text-navy/70 transition-colors"
        >
          Members
        </a>
      </header>

      <main className="flex-1 w-full max-w-4xl mx-auto px-6 pb-32">
        {/* Hero */}
        <section className="pt-20 pb-16">
          <p className="font-display text-[11px] uppercase tracking-[0.15em] text-navy/60 mb-6">
            Open until 31 August
          </p>
          <h1 className="font-display text-4xl md:text-5xl font-semibold leading-tight mb-8">
            An AI leadership coach, open to everyone until 31 August
          </h1>
          <p className="text-xl leading-relaxed max-w-2xl mb-8 font-body">
            Key Advisor Bot is trained on the KeyFutureSkills library — the scripts, frameworks and checklists built for people who got promoted on Friday and had to lead on Monday. Ask it anything about managing people.
          </p>
          <p className="font-display text-sm text-navy/80 mb-8">
            Ask your question in the chat window below.
          </p>
          
          <div className="inline-flex items-center px-3 py-1.5 border border-hairline bg-white/50 mb-12">
            <span className={`font-display text-xs font-medium uppercase tracking-wider ${isEndingSoon ? 'text-gold' : 'text-navy/50'}`}>
              {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining
            </span>
          </div>

          {/* Embedded Chatbot - Height maintained at 550px */}
          <div className="w-full border border-hairline bg-white p-1">
            <iframe 
              id="ai-chat-frame" 
              src="https://emb-chat.noform.ai/?pid=675408ef44b1bb5da3e857ee&backendURL=https://app.noform.ai" 
              className="w-full h-[550px] border-none"
              title="Key Advisor Bot Chat"
            />
          </div>
        </section>

        {/* What you can ask */}
        <section className="py-16 border-t border-hairline">
          <p className="font-display text-sm font-medium mb-8">Examples of what you can ask:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-hairline border border-hairline">
            {sampleQuestions.map((q, idx) => (
              <CopyCard key={idx} text={q} />
            ))}
          </div>
        </section>

        {/* Getting a useful answer */}
        <section className="py-16 border-t border-hairline">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <span className="font-display text-xs font-medium text-navy/40 mb-4 block">01</span>
              <p className="leading-relaxed">
                <strong className="font-bold">Give it the situation.</strong> Names, timelines, what has already been tried. A generic question gets a generic answer.
              </p>
            </div>
            <div>
              <span className="font-display text-xs font-medium text-navy/40 mb-4 block">02</span>
              <p className="leading-relaxed">
                <strong className="font-bold">Ask for the exact words.</strong> Say "give me the opening line" or "script the first two minutes" instead of asking what you should do.
              </p>
            </div>
            <div>
              <span className="font-display text-xs font-medium text-navy/40 mb-4 block">03</span>
              <p className="leading-relaxed">
                <strong className="font-bold">Push back on the answer.</strong> Tell it what felt wrong and ask again. The second draft is usually the one you can use.
              </p>
            </div>
          </div>
        </section>

        {/* What this is not */}
        <section className="py-16 border-t border-hairline">
          <div className="bg-white p-8 border border-hairline max-w-2xl">
            <p className="text-navy/80 leading-relaxed">
              Key Advisor Bot gives you language and structure for management conversations. It is not HR advice, legal advice, or a substitute for your own judgement about the people on your team. Anything involving discipline, dismissal or a formal complaint goes to your HR partner first.
            </p>
          </div>
        </section>
      </main>

      {/* Closing */}
      <section className="bg-navy text-cream py-24 w-full">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-3xl font-medium mb-6">
            When the window closes, the library stays open.
          </h2>
          <p className="text-lg leading-relaxed max-w-2xl mb-10 text-cream/80">
            The Manager Key: scripts, templates and checklists for difficult conversations, added to every month, plus the same AI coach without a deadline. $17/month, cancel anytime.
          </p>
          <a 
            href="https://member.keyfutureskills.com"
            className="inline-flex items-center justify-center px-6 py-3 border border-gold font-display text-sm font-medium hover:bg-gold hover:text-navy transition-colors duration-200"
          >
            See The Manager Key
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full border-t border-hairline py-12">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 font-display text-sm text-navy/60">
          <div className="flex flex-wrap gap-6">
            <a href="https://www.keyfutureskills.com" className="hover:text-navy transition-colors">Home</a>
            <a href="https://www.keyfutureskills.com/shop/" className="hover:text-navy transition-colors">Shop</a>
            <a href="https://www.keyfutureskills.com/category/blog/" className="hover:text-navy transition-colors">Blog</a>
          </div>
          <p>© {new Date().getFullYear()} KeyFutureSkills</p>
        </div>
      </footer>
    </div>
  );
}